import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  PartyPopper, Phone, ChevronRight, ChevronDown, CheckCircle2, MessageCircle, Wine, Music, Moon
} from "lucide-react";
import PageTransition from "../../components/PageTransition";
import PublicHeader from "../../components/PublicHeader";
import SeoFooter from "../../components/SeoFooter";

const HensBucksPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const ideas = [
    { icon: Wine, title: "Winery Day Trips", desc: "Mount Tamborine, Scenic Rim or the Sunshine Coast hinterland — cellar doors, long lunches and a driver who's always sober.", price: "Day charters from $650" },
    { icon: Music, title: "Brewery Crawls", desc: "Hop between Brisbane and Gold Coast's best craft breweries with your crew and your playlist on the bus.", price: "From $165 per transfer" },
    { icon: Moon, title: "Night Out Transfers", desc: "Dinner, bars, clubs and a safe ride home at the end — venue-to-venue transfers all night.", price: "From $165 per transfer" },
    { icon: PartyPopper, title: "Weekend Packages", desc: "Airport pickups, accommodation shuttles and the big day out — we run the whole weekend's transport.", price: "Custom quoted" }
  ];

  const included = [
    "Luxury Mercedes Sprinter (11 or 15 seats) with professional driver",
    "Bluetooth audio — your playlist, your vibe",
    "Door-to-door pickup for the whole group",
    "Fixed pricing quoted upfront, split-friendly",
    "Flexible itineraries — change stops on the day",
    "Safe, reliable late-night returns"
  ];

  const faqs = [
    { q: "How much does a hens or bucks party bus cost?", a: "Point-to-point transfers start from $165 in our 11-seater Sprinter. Full winery or brewery day charters typically start around $650–$800 depending on hours and distance. Message us on WhatsApp with your date and group size for an exact quote." },
    { q: "Can we drink on the bus?", a: "For private charters, BYO is generally fine — your bus, your rules (within reason and local regulations). We'll confirm the details when you book." },
    { q: "Where should we go for a hens party near Brisbane or the Gold Coast?", a: "Mount Tamborine wineries are the classic. The Scenic Rim (Kooroomba lavender farm + wineries), Sunshine Coast hinterland (Maleny/Montville) and Brisbane brewery crawls are equally brilliant. We'll help you build the itinerary." },
    { q: "How many people fit on one bus?", a: "Up to 15 per Sprinter. Bigger party? We run two buses in convoy so nobody gets left behind." },
    { q: "How far ahead should we book?", a: "Saturdays book out fast — 3–4 weeks ahead is safest for peak wedding season. Midweek and Sunday events can often be arranged with just a few days' notice." }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: "Hens and Bucks Party Transport",
        name: "Hens & Bucks Party Bus Hire - Limo Near Me",
        description: "Private hens and bucks party transport across Brisbane, Gold Coast and SEQ. Winery tours, brewery crawls and night-out transfers in luxury Mercedes Sprinter minibuses with driver.",
        provider: { "@type": "Organization", name: "Limo Near Me", telephone: "+61481437772", url: "https://www.limonearme.com.au" },
        areaServed: ["Brisbane", "Gold Coast", "Mount Tamborine", "Scenic Rim", "Byron Bay"]
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } }))
      }
    ]
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background" data-testid="hens-bucks-page">
        <Helmet>
          <title>Hens & Bucks Party Bus Hire Brisbane & Gold Coast | Winery Tours | Limo Near Me</title>
          <meta name="description" content="Hens party and bucks party transport across Brisbane & the Gold Coast. Private Mercedes Sprinter party buses with driver — winery tours to Mount Tamborine, brewery crawls & night-out transfers. Book online." />
          <meta name="keywords" content="hens party bus Brisbane, bucks party transport, party bus hire Gold Coast, hens winery tour, Mount Tamborine wine tour bus, bucks brewery tour" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/hens-bucks" />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>

        <PublicHeader />

        {/* Hero */}
        <section className="relative pt-24 min-h-[75vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/8682f6f4a960643f4a99dbc25f4720477d9cdc616952bfdd803802882d0ac59b.jpeg')" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/20 text-amber-300 mb-6 backdrop-blur-sm">
                <PartyPopper className="h-4 w-4" />
                <span className="text-sm font-medium">The party starts on the bus</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
                Hens & Bucks<br /><span className="text-amber-400">Party Transport</span>
              </h1>
              <p className="text-lg text-gray-200 mb-8">
                Winery tours, brewery crawls and big nights out across Brisbane, the Gold Coast and the hinterland.
                A private luxury minibus, your playlist on Bluetooth, and a professional driver so the whole crew
                celebrates together — no designated-driver drama.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/61481437772?text=Hi!%20Planning%20a%20hens%2Fbucks%20party%20—%20can%20I%20get%20a%20quote%3F" target="_blank" rel="noreferrer">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg bg-green-600 hover:bg-green-700 text-white" data-testid="hens-whatsapp-btn">
                    <MessageCircle className="mr-2 h-5 w-5" /> Get a Quote on WhatsApp
                  </Button>
                </a>
                <Link to="/book">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg font-bold" data-testid="hens-book-btn">
                    Book Online <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Ideas */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Pick Your Kind of Party</h2>
            <p className="text-muted-foreground text-center mb-12">Four ways to celebrate — or mix and match, it's your day</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ideas.map((idea, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl p-6 border border-black/5 hover:border-amber-300 hover:shadow-xl transition-all">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                    <idea.icon className="h-6 w-6 text-amber-700" />
                  </div>
                  <h3 className="font-bold mb-2">{idea.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{idea.desc}</p>
                  
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="py-16 bg-gray-950 text-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Every Party Booking Includes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {included.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl p-4">
                  <CheckCircle2 className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Hens & Bucks FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-black/10 p-5">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between text-left" data-testid={`hens-faq-${index}`}>
                    <h3 className="font-semibold pr-4">{faq.q}</h3>
                    <ChevronDown className={`h-5 w-5 text-amber-600 transition-transform flex-shrink-0 ${openFaq === index ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === index && <p className="text-muted-foreground text-sm mt-4 pt-4 border-t border-black/10">{faq.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-amber-50 text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4">Let's Plan Something Legendary</h2>
            <p className="text-muted-foreground mb-8">Send us the date, group size and the occasion — we'll send back a fixed quote and a suggested run sheet.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/61481437772?text=Hi!%20Planning%20a%20hens%2Fbucks%20party%20—%20can%20I%20get%20a%20quote%3F" target="_blank" rel="noreferrer">
                <Button size="lg" className="rounded-full px-10 py-6 text-lg bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp Us
                </Button>
              </a>
              <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg"><Phone className="mr-2 h-5 w-5" /> +61 481 437 772</Button></a>
            </div>
          </div>
        </section>

        <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default HensBucksPage;
